import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

export default function ServicePage({ title, subtitle, icon: Icon, providers, testId }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid={testId}>
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-white font-bold">{title}</h1>
            <p className="text-white/60 text-base mt-2 max-w-xl mx-auto">{subtitle}</p>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {providers.map((p, i) => (
              <motion.a
                key={p.name || p.url || `provider-${i}`}
                variants={fadeUp}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`provider-card-${i}`}
              >
                <div className={`h-2 bg-gradient-to-r ${p.color}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-white`}>
                        <p.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-charcoal">{p.name}</h3>
                        <span className="text-[10px] font-medium text-ocean/70 bg-ocean/5 px-2 py-0.5 rounded-full">{p.tagline}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-charcoal/30 group-hover:text-ocean transition-colors flex-shrink-0 mt-1" />
                  </div>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{p.desc}</p>
                  <div className="mt-4">
                    <Button size="sm" className={`w-full bg-gradient-to-r ${p.color} text-white rounded-full text-xs h-9 font-semibold`}>
                      Visit {p.name} <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.a>
            ))}
          </motion.div>

          <div className="flex justify-center mt-10">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
